/**
 * Fait : l'accès du client au FIL de conversation (lot 5.6) — le catalogue
 *   des modèles, les fils d'un projet, les branches et messages, lancer une
 *   génération, modifier son message, régénérer en branche, estimer avant de
 *   confirmer (critère 29) et comparer. Même contrat que `api.ts` : le refus
 *   du serveur est rendu tel quel, jamais réécrit.
 *
 * Ne fait pas : il n'ouvre pas le canal temps réel (l'écran s'en charge, une
 *   connexion par écran) et n'affiche rien.
 */

import { appelle, type Issue } from './appel.ts';

export type { Issue };

/** Une entrée du catalogue, telle que l'écran la choisit. */
export interface ModeleVu {
  readonly id: string;
  readonly libelle: string;
  readonly fournisseur: string;
  readonly voie: 'cle_api' | 'abonnement';

  /**
   * Le modèle est-il allumé ? La route rend TOUT le catalogue à qui
   * l'administre — c'est ce qu'il faut pour l'administrer — et ne filtre que
   * pour les autres. Sans ce champ, l'écran du fil ne pouvait pas faire la
   * différence, et un parent administrateur continuait de se voir proposer
   * les modèles qu'il venait lui-même d'éteindre.
   */
  readonly actif: boolean;
}

/** Un fil de conversation dans la liste. */
export interface FilVu {
  readonly id: string;
  readonly titre: string;
  readonly version: number;
}

/** Une branche d'un fil. */
export interface BrancheVue {
  readonly id: string;
  readonly messageDOrigineId: string | null;
}

/** Un message du fil, tel que l'écran l'affiche. */
export interface MessageVu {
  readonly id: string;
  readonly origine: 'membre' | 'assistant';
  readonly rang: number;
  readonly texte: string;
  readonly version: number;
  readonly auteurId?: string;
  readonly declencheurId?: string;
  readonly identifiantModele?: string;
}

/** L'estimation d'un modèle avant confirmation (critère 29). */
export interface EstimationVue {
  readonly nombreDAppels: number;
  readonly parModele: readonly {
    readonly entreeDeCatalogueId: string;
    readonly libelle: string;
    readonly voie: 'cle_api' | 'abonnement';
    readonly estimationHauteCentimes?: number;
  }[];
}

/**
 * Les modèles ALLUMÉS du catalogue — jamais un identifiant en dur ici.
 *
 * LE FILTRE EST ICI, ET IL N'ÉTAIT NULLE PART. Cette fonction a longtemps
 * promis « les modèles actifs » en rendant tel quel ce que la route donne — or
 * la route rend le catalogue ENTIER à qui administre la famille, éteints
 * compris, parce qu'il lui faut bien ça pour le tenir. Un parent
 * administrateur se voyait donc proposer, dans le composeur comme dans le
 * comparateur, les modèles qu'il venait lui-même d'éteindre : le seul écran où
 * son geste n'avait aucun effet était le sien. Le serveur, lui, refusait déjà
 * de lancer dessus — la porte était fermée, mais on continuait de la montrer.
 */
export async function listeLeCatalogue(): Promise<Issue<{ modeles: ModeleVu[] }>> {
  const issue = await appelle<{ modeles: ModeleVu[] }>('/api/catalogue');

  if (!issue.bon) return issue;

  return { bon: true, valeur: { modeles: issue.valeur.modeles.filter((modele) => modele.actif) } };
}

/** Les fils d'un projet. */
export function listeLesFils(projetId: string) {
  return appelle<{ conversations: FilVu[] }>(`/api/projets/${projetId}/conversations`);
}

/** Crée un fil, et rend sa branche initiale avec lui. */
export function creeUnFil(projetId: string, titre: string) {
  return appelle<{ conversationId: string; brancheId: string }>(
    `/api/projets/${projetId}/conversations`,
    { methode: 'POST', corps: { titre } },
  );
}

/** Les branches d'un fil, l'initiale d'abord. */
export function listeLesBranches(filId: string) {
  return appelle<{ branches: BrancheVue[] }>(`/api/conversations/${filId}/branches`);
}

/** Les messages d'une branche, dans l'ordre. */
export function listeLesMessagesDuFil(filId: string, brancheId: string) {
  return appelle<{ messages: MessageVu[] }>(
    `/api/conversations/${filId}/messages?branche=${brancheId}`,
  );
}

/** Lance une génération — la réponse arrive ensuite par le canal. */
export function lanceUneGeneration(
  filId: string,
  brancheId: string,
  entreeDeCatalogueId: string,
  texte: string,
) {
  return appelle<{ generationId: string }>(`/api/conversations/${filId}/generations`, {
    methode: 'POST',
    corps: { brancheId, entreeDeCatalogueId, texte },
  });
}

/** Modifie SON message — la version est obligatoire (arbitrage). */
export function modifieUnMessage(messageId: string, texte: string, version: number) {
  return appelle<void>(`/api/messages/${messageId}`, {
    methode: 'PATCH',
    corps: { texte, version },
  });
}

/** Régénère : nouvelle branche depuis ce message, nouvelle réponse. */
export function regenereEnBranche(
  filId: string,
  messageDOrigineId: string,
  entreeDeCatalogueId: string,
) {
  return appelle<{ brancheId: string; generationId: string }>(
    `/api/conversations/${filId}/branches`,
    { methode: 'POST', corps: { messageDOrigineId, entreeDeCatalogueId } },
  );
}

/** L'estimation AVANT confirmation — le critère 29 l'exige à l'écran. */
export function estimeLaComparaison(
  filId: string,
  brancheId: string,
  entreesDeCatalogueIds: string[],
  texte: string,
) {
  return appelle<EstimationVue>(`/api/conversations/${filId}/estimations`, {
    methode: 'POST',
    corps: { brancheId, entreesDeCatalogueIds, texte },
  });
}

/** Lance la comparaison confirmée — une branche par modèle. */
export function lanceLaComparaison(
  filId: string,
  brancheId: string,
  entreesDeCatalogueIds: string[],
  texte: string,
) {
  return appelle<{
    lancements: { entreeDeCatalogueId: string; brancheId: string; issue: string }[];
  }>(`/api/conversations/${filId}/comparaisons`, {
    methode: 'POST',
    corps: { brancheId, entreesDeCatalogueIds, texte },
  });
}
